import { useCallback, useMemo } from 'react';
import { stableStringify } from '@/shared/lib';
import { useSettings } from './useSettings';
import {
  TABLE_PRESET_SNAPSHOT_KEYS,
  TableSettings,
  type TablePresetSnapshotKey,
  type TableViewSnapshot,
} from './types';

/** Описание пресета для UI: id-ключ в `tables`, имя и снимок настроек. */
export type TablePreset = {
  /** Полный ключ в `tables` (формат `<tableName>:<uuid>`) */
  id: string;
  /** Пользовательское имя пресета */
  name: string;
  /** Источник внешней выборки, из которой создан пресет; `undefined` — пресет заведён вручную */
  selectionSource?: string;
  /** Пресет закреплён отдельным пунктом левого меню */
  pinned: boolean;
  /** Снимок настроек таблицы (только поля из TABLE_PRESET_SNAPSHOT_KEYS) */
  settings: TableSettings;
};

/**
 * Стабильное сериализующее сравнение двух снимков пресета.
 * Используется для определения «грязного» состояния (текущее ≠ активный пресет).
 * Сравниваются только поля из {@link TABLE_PRESET_SNAPSHOT_KEYS}.
 */
const stringifySnapshot = (settings: TableSettings | undefined): string => {
  if (!settings) return '{}';
  const parts: string[] = [];
  for (const key of TABLE_PRESET_SNAPSHOT_KEYS) {
    parts.push(`${key}=${stableStringify(settings[key])}`);
  }
  return parts.join('|');
};

/**
 * Извлекает префиксированный ключ таблицы из ключа `tables`.
 * Возвращает `null`, если ключ не относится к указанной таблице (нет префикса).
 */
const PRESET_KEY_SEPARATOR = ':';
const isPresetKeyOfTable = (key: string, tableName: string): boolean =>
  key.startsWith(`${tableName}${PRESET_KEY_SEPARATOR}`);

/**
 * Хук работы с именованными пресетами настроек таблицы.
 *
 * Пресеты хранятся в `settings.tables` под ключами `<tableName>:<uuid>` и отличаются
 * от рабочей записи `tables[tableName]` наличием поля `name`. Активный пресет
 * хранится в `tables[tableName].activePresetId`.
 *
 * @param tableName - Имя таблицы (например, `'dialogsListV2'`).
 */
export const useTablePresets = (tableName: string) => {
  const {
    userId,
    tables,
    saveTablePreset,
    updateTablePreset,
    applyTablePreset,
    enterTableView,
    deleteTablePreset,
    renameTablePreset,
    setTablePresetPinned,
    clearActiveTablePreset,
    exitTableSelection,
  } = useSettings();

  const presets = useMemo<TablePreset[]>(() => {
    const list: TablePreset[] = [];
    for (const [key, value] of Object.entries(tables)) {
      if (!isPresetKeyOfTable(key, tableName)) continue;
      if (!value.name) continue;
      list.push({
        id: key,
        name: value.name,
        selectionSource: value.selectionSource,
        pinned: value.pinnedToMenu === true,
        settings: value,
      });
    }
    list.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    return list;
  }, [tables, tableName]);

  const activeRecord = tables[tableName];
  const activePresetId = activeRecord?.activePresetId;
  const activePreset = useMemo(() => presets.find((p) => p.id === activePresetId) ?? null, [presets, activePresetId]);

  const isDirty = useMemo(() => {
    if (!activePreset) return false;
    return stringifySnapshot(activeRecord) !== stringifySnapshot(activePreset.settings);
  }, [activePreset, activeRecord]);

  const savePresetAs = useCallback(
    (name: string, selectionSource?: string): string => saveTablePreset(userId, tableName, name, selectionSource),
    [saveTablePreset, userId, tableName],
  );

  const saveCurrentToActivePreset = useCallback(() => {
    if (!activePresetId) return;
    updateTablePreset(userId, tableName, activePresetId);
  }, [updateTablePreset, userId, tableName, activePresetId]);

  const apply = useCallback(
    (presetId: string) => applyTablePreset(userId, tableName, presetId),
    [applyTablePreset, userId, tableName],
  );

  const enterView = useCallback(
    (view: TableViewSnapshot, options?: { backup?: boolean }) => enterTableView(userId, tableName, view, options),
    [enterTableView, userId, tableName],
  );

  const remove = useCallback((presetId: string) => deleteTablePreset(userId, presetId), [deleteTablePreset, userId]);

  const rename = useCallback(
    (presetId: string, name: string) => renameTablePreset(userId, presetId, name),
    [renameTablePreset, userId],
  );

  const setPinned = useCallback(
    (presetId: string, pinned: boolean) => setTablePresetPinned(userId, presetId, pinned),
    [setTablePresetPinned, userId],
  );

  const clearActive = useCallback(
    () => clearActiveTablePreset(userId, tableName),
    [clearActiveTablePreset, userId, tableName],
  );

  const exitSelection = useCallback(
    (selectionFilterId: string) => exitTableSelection(userId, tableName, selectionFilterId),
    [exitTableSelection, userId, tableName],
  );

  return {
    presets,
    activePresetId,
    activePreset,
    isDirty,
    savePresetAs,
    saveCurrentToActivePreset,
    apply,
    enterView,
    remove,
    rename,
    setPinned,
    clearActive,
    exitSelection,
  };
};

// Чтобы тип TablePresetSnapshotKey не выпал при анализе использований
export type { TablePresetSnapshotKey };
